import React, { useMemo, useState } from 'react';
import { PowerDialerBar } from '@/components/outbound/PowerDialerBar';
import { usePowerDialer } from '@/hooks/usePowerDialer';
import { useParams, useNavigate } from 'react-router-dom';
import { useContactLists, useContactListDetail } from '@/hooks/useContactLists';
import { useNotes } from '@/hooks/useNotes';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import DealForm from '@/components/deals/DealForm';
import { supabase } from '@/integrations/supabase/client';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ArrowLeft, Phone, MessageCircle, MoreHorizontal, ExternalLink, StickyNote, Trash2, UserPlus, Search, Loader2, Briefcase, CheckSquare, X } from 'lucide-react';
import { format } from 'date-fns';
import { safeFormat } from '@/lib/safeDate';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';
import { useTwilio } from '@/contexts/TwilioContext';
import { formatPhoneNumber, getFullPhoneE164 } from '@/lib/utils';
import { ChatCore, ContactChatHeader } from '@/features/chat';
import { useContactSearch } from '@/hooks/useContactSearch';

function ContactNotes({ contactId }) {
  const { notes, loading, createNote } = useNotes({ contactId });
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!note.trim()) return;
    setSaving(true);
    try {
      await createNote({ content: note.trim() });
      setNote('');
      toast.success('Nota salva');
    } catch { /* hook handles error */ }
    setSaving(false);
  };

  return (
    <div className="px-4 pb-3 border-t pt-3 space-y-3">
      <Textarea
        placeholder="Anotações sobre este contato..."
        value={note}
        onChange={(e) => setNote(e.target.value)}
        className="text-sm min-h-[80px] resize-none"
        autoFocus
      />
      <div className="flex gap-2">
        <Button size="sm" onClick={handleSave} disabled={saving || !note.trim()}>
          {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
          Salvar nota
        </Button>
      </div>

      {loading && <p className="text-xs text-muted-foreground">Carregando notas...</p>}
      {!loading && notes.length > 0 && (
        <div className="space-y-2 max-h-40 overflow-y-auto">
          {notes.map((n) => (
            <div key={n.id} className="text-sm bg-muted/50 rounded-lg p-2.5">
              <p className="text-foreground whitespace-pre-wrap">{n.content}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {n.creator?.display_name && `${n.creator.display_name} · `}
                {safeFormat(new Date(n.created_at), "d MMM yyyy 'às' HH:mm", { locale: ptBR })}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const OUTCOME_LABEL = {
  human: 'Humano',
  machine: 'Caixa postal',
  no_answer: 'Não atendeu',
  busy: 'Ocupado',
  failed: 'Falhou',
  cancelled: 'Cancelada',
};

function ContactCard({ item, lastOutcome, onRemove, selectionMode, selected, onToggleSelect, createDeal }) {
  const [showWhatsApp, setShowWhatsApp] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [showDealForm, setShowDealForm] = useState(false);
  const { makeCall } = useTwilio();

  const c = item.contact;
  if (!c) return null;

  const fullName = c.preferred_name || [c.first_name, c.last_name].filter(Boolean).join(' ') || '—';
  const phoneE164 = c.primary_phone
    ? getFullPhoneE164(c.primary_phone, c.primary_phone_country_code)
    : null;
  const phoneFormatted = c.primary_phone
    ? formatPhoneNumber(c.primary_phone, c.primary_phone_country_code)
    : null;

  const handleCall = async () => {
    if (!phoneE164) { toast.error('Contato sem telefone'); return; }
    try {
      await makeCall(phoneE164, c.id);
      toast.success(`Ligando para ${fullName}...`);
    } catch {
      toast.error('Erro ao iniciar ligação');
    }
  };

  return (
    <>
      <div className={`rounded-xl border bg-card shadow-sm hover:shadow-md transition-shadow ${selected ? 'ring-2 ring-primary' : ''}`}>
        <div className="flex items-start gap-3 p-4">
          {selectionMode && (
            <div className="flex items-center pt-1">
              <Checkbox
                checked={selected}
                onCheckedChange={() => onToggleSelect(item.id)}
              />
            </div>
          )}

          {/* Avatar */}
          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0 text-primary font-semibold text-sm">
            {(c.first_name?.[0] ?? '?').toUpperCase()}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <a href={`/contact-detail?id=${c.id}`} className="font-semibold text-sm hover:underline hover:text-primary cursor-pointer">{fullName}</a>
              {lastOutcome && (
                <span className="rounded-full border bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                  {OUTCOME_LABEL[lastOutcome] ?? lastOutcome}
                </span>
              )}
              {c.do_not_call && (
                <span className="rounded-full border border-destructive/30 bg-destructive/10 px-2 py-0.5 text-[11px] text-destructive">Não ligar</span>
              )}
            </div>
            <div className="flex items-center gap-3 mt-0.5 text-sm text-muted-foreground flex-wrap">
              {phoneFormatted && <span>{phoneFormatted}</span>}
              {c.primary_email && <span className="truncate">{c.primary_email}</span>}
            </div>
          </div>

          {/* Action buttons */}
          {!selectionMode && (
            <div className="flex items-center gap-1 shrink-0">
              <Button
                size="sm"
                variant="default"
                className="h-8 px-3"
                onClick={handleCall}
                disabled={!phoneE164}
                title="Ligar"
              >
                <Phone className="h-4 w-4 mr-1" />
                Ligar
              </Button>

              <Button
                size="sm"
                variant="outline"
                className="h-8 w-8 p-0 text-green-700 border-green-200 hover:bg-green-50"
                onClick={() => setShowWhatsApp(true)}
                disabled={!c.primary_phone}
                title="WhatsApp"
              >
                <MessageCircle className="h-4 w-4" />
              </Button>

              <Button
                size="sm"
                variant="outline"
                className="h-8 w-8 p-0 text-blue-700 border-blue-200 hover:bg-blue-50"
                onClick={() => setShowDealForm(true)}
                title="Criar Deal"
              >
                <Briefcase className="h-4 w-4" />
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setNotesOpen(v => !v)}>
                    <StickyNote className="h-4 w-4 mr-2" />
                    Adicionar nota
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => onRemove(item.id)} className="text-destructive focus:text-destructive">
                    <Trash2 className="h-4 w-4 mr-2" />
                    Remover da lista
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>

        {notesOpen && <ContactNotes contactId={c.id} />}
      </div>

      {/* WhatsApp Sheet */}
      <Sheet open={showWhatsApp} onOpenChange={setShowWhatsApp}>
        <SheetContent side="right" className="w-full sm:max-w-lg p-0 flex flex-col">
          <SheetHeader className="px-4 pt-4 pb-2 border-b shrink-0">
            <SheetTitle className="text-base">WhatsApp — {fullName}</SheetTitle>
            {phoneFormatted && <p className="text-xs text-muted-foreground -mt-1">{phoneFormatted}</p>}
          </SheetHeader>
          <div className="flex-1 overflow-hidden">
            <ChatCore
              contactId={c.id}
              contactPhone={phoneE164 ?? undefined}
              showConversationSelector
              header={
                <ContactChatHeader
                  phoneNumber={phoneE164 ?? undefined}
                  isConnected
                />
              }
            />
          </div>
        </SheetContent>
      </Sheet>
      {/* Deal creation dialog */}
      <Dialog open={showDealForm} onOpenChange={setShowDealForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Criar Deal — {fullName}</DialogTitle>
          </DialogHeader>
          <DealForm
            initialContactId={c.id}
            onSubmit={async (dealData) => {
              await createDeal({ ...dealData, contact_id: c.id });
              setShowDealForm(false);
              toast.success('Deal criado com sucesso!');
            }}
            onCancel={() => setShowDealForm(false)}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function ListDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { lists, addToList, isAdding } = useContactLists();
  const { items, isLoading, removeItem, removeItems, isRemoving } = useContactListDetail(id);
  const createDeal = async (deal) => {
    const { data, error } = await supabase.from('deals').insert(deal).select().single();
    if (error) throw error;
    return data;
  };

  const [showAddContact, setShowAddContact] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { contacts: searchResults, loading: searchLoading } = useContactSearch(searchQuery);

  // Selection mode
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const list = lists.find((l) => l.id === id);

  // Power Dialer: fila derivada dos itens da lista (contatos únicos)
  const dialerQueue = useMemo(() => {
    const seen = new Set();
    return items
      .filter((i) => i.contact && !seen.has(i.contact.id) && seen.add(i.contact.id))
      .map((i) => ({ id: i.id, status: 'pending', contact_id: i.contact.id, contact: i.contact }));
  }, [items]);
  const { attempts: dialerAttempts } = usePowerDialer({ autoManage: false });
  const outcomeByContact = useMemo(() => {
    const map = {};
    for (const a of dialerAttempts || []) {
      if (a.status === 'completed' && a.contact_id && !map[a.contact_id]) map[a.contact_id] = a.outcome;
    }
    return map;
  }, [dialerAttempts]);

  const existingContactIds = new Set(items.map(i => i.contact_id).filter(Boolean));
  const filteredResults = searchResults.filter(c => !existingContactIds.has(c.id));

  const handleAddContact = async (contactId) => {
    try {
      await addToList({ listId: id, contactIds: [contactId] });
      setSearchQuery('');
      setShowAddContact(false);
    } catch { /* toast already handled by hook */ }
  };

  const toggleSelect = (itemId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  const selectAll = () => {
    setSelectedIds(new Set(items.map(i => i.id)));
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    setSelectionMode(false);
  };

  const handleBulkRemove = async () => {
    if (selectedIds.size === 0) return;
    try {
      await removeItems([...selectedIds]);
      clearSelection();
    } catch { /* hook handles error */ }
  };

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">
      <div className="flex-shrink-0 p-4 md:p-6">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="flex items-center gap-3 mb-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/listas')} className="rounded-full h-9 w-9 p-0">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="flex-1">
              <h1 className="text-xl font-bold text-foreground">{list?.name || 'Lista'}</h1>
              {list?.description && <p className="text-sm text-muted-foreground mt-0.5">{list.description}</p>}
            </div>
            <div className="flex items-center gap-2">
              {!selectionMode && items.length > 0 && (
                <Button size="sm" variant="outline" onClick={() => setSelectionMode(true)}>
                  <CheckSquare className="h-4 w-4 mr-1" />
                  Selecionar
                </Button>
              )}
              <Button size="sm" onClick={() => setShowAddContact(true)}>
                <UserPlus className="h-4 w-4 mr-1" />
                Adicionar contato
              </Button>
            </div>
          </div>

          {/* Selection toolbar */}
          {selectionMode && (
            <div className="flex items-center gap-3 mb-4 p-3 rounded-lg bg-primary/5 border border-primary/20">
              <span className="text-sm font-medium text-foreground">
                {selectedIds.size} {selectedIds.size === 1 ? 'selecionado' : 'selecionados'}
              </span>
              <Button size="sm" variant="outline" onClick={selectAll} className="h-7 text-xs">
                Selecionar todos
              </Button>
              <div className="flex-1" />
              {selectedIds.size > 0 && (
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={handleBulkRemove}
                  disabled={isRemoving}
                  className="h-7 text-xs"
                >
                  {isRemoving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Trash2 className="h-3 w-3 mr-1" />}
                  Remover ({selectedIds.size})
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={clearSelection} className="h-7 w-7 p-0">
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}

          {list && (
            <div className="flex items-center gap-4 mb-6 text-sm text-muted-foreground">
              <span>{items.length} {items.length === 1 ? 'contato' : 'contatos'}</span>
              <span>·</span>
              <span>Criada em {safeFormat(new Date(list.created_at), "d 'de' MMMM 'de' yyyy", { locale: ptBR })}</span>
            </div>
          )}

          {!isLoading && dialerQueue.length > 0 && (
            <div className="mb-6">
              <PowerDialerBar queue={dialerQueue} batchLabel={`Lista: ${list?.name || ''}`} />
            </div>
          )}

          {/* Content */}
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <p className="text-muted-foreground">Esta lista está vazia.</p>
              <Button size="sm" variant="outline" className="mt-3" onClick={() => setShowAddContact(true)}>
                <UserPlus className="h-4 w-4 mr-1" />
                Adicionar contato
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {items.map((item) => (
                <ContactCard
                  key={item.id}
                  item={item}
                  lastOutcome={item.contact ? outcomeByContact[item.contact.id] : undefined}
                  onRemove={removeItem}
                  selectionMode={selectionMode}
                  selected={selectedIds.has(item.id)}
                  onToggleSelect={toggleSelect}
                  createDeal={createDeal}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add Contact Dialog */}
      <Dialog open={showAddContact} onOpenChange={(open) => { setShowAddContact(open); if (!open) setSearchQuery(''); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Adicionar contato à lista</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nome, telefone ou email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                autoFocus
              />
            </div>
            <div className="max-h-64 overflow-y-auto space-y-1">
              {searchLoading && (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              )}
              {!searchLoading && searchQuery.length >= 1 && filteredResults.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-6">Nenhum contato encontrado.</p>
              )}
              {filteredResults.map((c) => {
                const name = [c.first_name, c.last_name].filter(Boolean).join(' ') || '—';
                return (
                  <button
                    key={c.id}
                    onClick={() => handleAddContact(c.id)}
                    disabled={isAdding}
                    className="w-full flex items-center gap-3 p-2.5 rounded-lg text-left hover:bg-accent transition-colors disabled:opacity-50"
                  >
                    <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0 text-primary font-semibold text-xs">
                      {(c.first_name?.[0] ?? '?').toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{name}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {c.primary_email || c.primary_phone || ''}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
